import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Users, Copy, Check, Send, Mail, ArrowRight, ArrowLeft, Link as LinkIcon, UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import sixthLogo from '../assets/icon.svg';
import { local } from '../utils/constants';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

// Animations
const floatOrb = keyframes`
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.6;
  }
  25% {
    transform: translate(30px, -40px) scale(1.05);
    opacity: 0.8;
  }
  50% {
    transform: translate(-20px, -60px) scale(1.1);
    opacity: 0.7;
  }
  75% {
    transform: translate(-40px, -20px) scale(0.95);
    opacity: 0.65;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Background Orbs
const BackgroundOrb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Orb1 = styled(BackgroundOrb)`
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(224, 5, 255, 0.25) 0%, transparent 70%);
  top: -10%;
  left: -5%;
  animation: ${floatOrb} 20s ease-in-out infinite;
`;

const Orb2 = styled(BackgroundOrb)`
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(109, 40, 217, 0.3) 0%, transparent 70%);
  top: 40%;
  right: -10%;
  animation: ${floatOrb} 25s ease-in-out infinite reverse;
  animation-delay: -5s;
`;

// Container
const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  position: relative;
  overflow-x: hidden;
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

// Header
const Header = styled.header`
  background: rgba(10, 10, 15, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(224, 5, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1rem 0;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  img {
    width: 32px;
    height: 32px;
    filter: drop-shadow(0 0 8px rgba(224, 5, 255, 0.5));
  }
`;

const DashboardLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #E005FF;
    background: rgba(224, 5, 255, 0.1);
  }
`;

// Main Content
const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  position: relative;
  z-index: 1;
`;

const FormCard = styled(motion.div)`
  background: rgba(18, 18, 25, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(224, 5, 255, 0.2);
  border-radius: 24px;
  padding: 48px;
  max-width: 650px;
  width: 100%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const SuccessBanner = styled(motion.div)`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.1));
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(16, 185, 129, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10b981;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const SuccessText = styled.div`
  flex: 1;

  h3 {
    color: #10b981;
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 4px 0;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(224, 5, 255, 0.2), rgba(167, 60, 255, 0.1));
  border: 2px solid rgba(224, 5, 255, 0.3);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #E005FF;
`;

const FormTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 12px 0;
`;

const FormSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const TeamName = styled.span`
  color: #E005FF;
  font-weight: 600;
`;

// Sections
const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #E005FF;
  }
`;

const SectionDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

// Invite Link
const InviteLinkContainer = styled.div`
  background: rgba(10, 10, 15, 0.8);
  border: 1px solid rgba(224, 5, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const InviteLinkInput = styled.input`
  flex: 1;
  padding: 16px 20px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Monaco', 'Menlo', monospace;

  &:focus {
    outline: none;
  }
`;

const CopyButton = styled(motion.button)`
  padding: 16px 24px;
  background: ${props => props.copied ? 'rgba(16, 185, 129, 0.2)' : 'rgba(224, 5, 255, 0.1)'};
  border: none;
  border-left: 1px solid rgba(224, 5, 255, 0.2);
  color: ${props => props.copied ? '#10b981' : '#E005FF'};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.copied ? 'rgba(16, 185, 129, 0.3)' : 'rgba(224, 5, 255, 0.2)'};
  }
`;

// Email Invite
const EmailInput = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  background: rgba(10, 10, 15, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #E005FF;
    box-shadow: 0 0 0 3px rgba(224, 5, 255, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const EmailHint = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  margin: 8px 0 0 0;
`;

// Buttons
const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const SendInvitesButton = styled(motion.button)`
  flex: 1;
  padding: 16px 24px;
  background: linear-gradient(135deg, #E005FF 0%, #A73CFF 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SkipButton = styled(motion.button)`
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

// Messages
const Message = styled(motion.div)`
  padding: 16px;
  border-radius: 12px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;

  ${props => props.type === 'success' && `
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
  `}

  ${props => props.type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #FCA5A5;
  `}
`;

const InviteMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teamId, setTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [emails, setEmails] = useState('');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Get team info from localStorage or URL params
    const storedTeamId = localStorage.getItem('pending_team_id');
    const storedTeamName = localStorage.getItem('pending_team_name');
    const urlParams = new URLSearchParams(location.search);
    const urlTeamId = urlParams.get('team_id');
    const sessionId = urlParams.get('session_id');

    const finalTeamId = urlTeamId || storedTeamId;
    const finalTeamName = storedTeamName || 'Your Team';

    // If session_id is present, activate the team (workaround for localhost Stripe testing)
    const activateTeam = async () => {
      if (sessionId) {
        try {
          const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || localStorage.getItem('vscode_jwt_token') || sessionStorage.getItem('jwt_token');

          const response = await fetch(`${serverBaseUrl}/teams/activate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ session_id: sessionId })
          });

          const data = await response.json();

          if (response.ok) {
            console.log('Team activated successfully:', data);
            // Clean up URL by removing session_id
            const newParams = new URLSearchParams(location.search);
            newParams.delete('session_id');
            const cleanUrl = location.pathname + (newParams.toString() ? '?' + newParams.toString() : '');
            window.history.replaceState({}, document.title, cleanUrl);
          } else {
            console.error('Failed to activate team:', data.message || data.detail);
          }
        } catch (err) {
          console.error('Error activating team:', err);
        }
      }
    };

    if (finalTeamId) {
      setTeamId(finalTeamId);
      setTeamName(finalTeamName);
      // Generate invite link
      const baseUrl = local ? 'http://localhost:3000' : 'https://app.trysixth.com';
      setInviteLink(`${baseUrl}/vscode/signup?referrer=teams&team_id=${finalTeamId}`);
      setShowSuccess(true);

      // Activate team if session_id is present
      activateTeam();
    }
  }, [location]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateEmails = (emailString) => {
    const emailList = emailString.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailList.every(email => emailRegex.test(email));
  };

  const handleSendInvites = async () => {
    if (!emails.trim()) {
      setMessage({ type: 'error', text: 'Please enter at least one email address' });
      return;
    }

    if (!validateEmails(emails)) {
      setMessage({ type: 'error', text: 'Please enter valid email addresses' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || localStorage.getItem('vscode_jwt_token') || sessionStorage.getItem('jwt_token');

      const emailList = emails.split(',').map(e => e.trim()).filter(e => e);

      const response = await fetch(`${serverBaseUrl}/teams/send_invites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_id: teamId,
          emails: emailList,
          invite_link: inviteLink
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        setMessage({ type: 'success', text: `Invitations sent to ${data.sent_count || emailList.length} email(s)!` });
        setEmails('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || errorData.detail || 'Failed to send invitations' });
      }
    } catch (err) {
      console.error('Send invites error:', err);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  const handleGoToDashboard = () => {
    // Clean up localStorage
    localStorage.removeItem('pending_team_id');
    localStorage.removeItem('pending_team_name');
    navigate('/vscode/dashboard');
  };

  return (
    <Container>
      <Orb1 />
      <Orb2 />

      <Header>
        <Nav>
          <Logo to="/">
            <img src={sixthLogo} alt="Sixth AI" />
            Sixth AI
          </Logo>
          <DashboardLink to="/vscode/dashboard">
            Go to Dashboard
            <ArrowRight size={18} />
          </DashboardLink>
        </Nav>
      </Header>

      <MainContent>
        <FormCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatePresence>
            {showSuccess && (
              <SuccessBanner
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <SuccessIcon>
                  <CheckCircle size={24} />
                </SuccessIcon>
                <SuccessText>
                  <h3>Team Created Successfully!</h3>
                  <p>Your team is ready. Now invite your team members to get started.</p>
                </SuccessText>
              </SuccessBanner>
            )}
          </AnimatePresence>

          <FormHeader>
            <IconWrapper>
              <UserPlus size={40} />
            </IconWrapper>
            <FormTitle>Invite Team Members</FormTitle>
            <FormSubtitle>
              Share the invite link or send email invitations to add members to <TeamName>{teamName}</TeamName>
            </FormSubtitle>
          </FormHeader>

          <Section>
            <SectionTitle>
              <LinkIcon size={20} />
              Share Invite Link
            </SectionTitle>
            <SectionDescription>
              Copy this link and share it with your team members. They can use it to join your team.
            </SectionDescription>
            <InviteLinkContainer>
              <InviteLinkInput
                type="text"
                value={inviteLink}
                readOnly
              />
              <CopyButton
                copied={copied}
                onClick={handleCopy}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? 'Copied!' : 'Copy'}
              </CopyButton>
            </InviteLinkContainer>
          </Section>

          <Section>
            <SectionTitle>
              <Mail size={20} />
              Send Email Invitations
            </SectionTitle>
            <SectionDescription>
              Enter email addresses to send invitation emails directly to your team members.
            </SectionDescription>
            <EmailInput
              placeholder="john@company.com, jane@company.com, mike@company.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
            <EmailHint>Separate multiple email addresses with commas</EmailHint>

            <AnimatePresence>
              {message.text && (
                <Message
                  type={message.type}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                  {message.text}
                </Message>
              )}
            </AnimatePresence>
          </Section>

          <ButtonGroup>
            <SendInvitesButton
              onClick={handleSendInvites}
              disabled={sending || !emails.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {sending ? 'Sending...' : 'Send Invitations'}
              <Send size={18} />
            </SendInvitesButton>
            <SkipButton
              onClick={handleGoToDashboard}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Skip for Now
              <ArrowRight size={18} />
            </SkipButton>
          </ButtonGroup>
        </FormCard>
      </MainContent>
    </Container>
  );
};

export default InviteMembers;
